/**
 * The little black book (order 761): the period stand-in for a phone. One place for the jobs and what to
 * do next, the inbox, the contacts, money, your motor, the gang and crew (online), the map, how to play and
 * the settings.
 *
 *  - opens on HOME (an index of tiles under a "Right now" slip, bookScreen.ts) with I (desktop), D-pad down
 *    (pad) or the BOOK knob in the touch system row; the bleeper opens MESSAGES; other code turns straight
 *    to a section (`open('jobs')`, a message's button) and back then shuts the book;
 *  - the bleeper (a pocket pager, HUD top right on desktop; the BOOK knob's badge on touch) counts unread
 *    messages, bleeps and buzzes when one arrives, and opens MESSAGES;
 *  - offline the world freezes while the book is open (like a menu), online it keeps going;
 *  - the offline inbox is saved in localStorage (`bh.book.v1`) and wiped by New game; the online inbox
 *    lives for the session and is fed by src/online-ui/bookFeed.ts, which also fills the GANG / CREW /
 *    STREET / MONEY / YOUR MOTOR pages, HOME's slip and street tiles through `setOnlineProvider` (they open
 *    the existing online panels).
 */
import { playerLockupPoi } from '../economy/playerLockup';
import './book.css';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { touchUi } from '../input/touch';
import { el, setText, toggle } from '../ui/dom';
import { CONTROL_ICONS } from '../ui/icons';
import { haptic } from '../ui/mobile';
import { HELP_LOOP } from '../ui/helpScreen';
import { formatDistance } from '../ui/format';
import { formatMoney, LOCKUP_PRICE, LOCKUP_UNLOCK_ID } from '../economy/wallet';
import type { JobBoardEntry } from '../missions/missionSystem';
import { MEDAL_NAME, MEDAL_RULES, MEDAL_STAMP, formatRunTime } from '../missions/medals';
import { fareText, minicabFare } from '../missions/minicab';
import { BRIBE_PRICE, SERGEANT } from '../heat/deskSergeant';
import { BookScreen, TAB_ICONS, TAB_LABELS, type BookButton, type BookEntry, type BookHome, type BookNow, type BookPage, type BookPages, type BookTab, type BookTile, type BookTileGroup, type BookView } from './bookScreen';
import { Inbox, agoText, type BookMessage, type MessageAction, type PostInput } from './inbox';
import { wireOfflineFeed } from './offlineFeed';
import { CONTACTS, FACES, contactPos } from './contacts';
import { contactPerk } from '../contacts/perks';
import { TRACKS, bestTitle, tracksFor, discounted, type ContactTrack } from '../../shared/contacts';
import { ladderFor, standingName } from './contactLadder';
import { offlineRecordPage } from './recordPage';
import { MISSIONS } from '../missions/missions';
import { emptyActivities } from '../activities/state';
import { defaultStats } from '../save/saveData';

export type { BookTab, BookView, BookEntry, BookPage, BookNow, BookTile, BookTileGroup } from './bookScreen';
export type { BookMessage, PostInput, MessageAction } from './inbox';

/** What src/online-ui adds to the book while online (it renders from its own store). */
export interface OnlineBookProvider {
  /** Whole pages it owns: gang, crew, street, money, motor (null = use the book's offline page). */
  page(tab: BookTab): BookPage | null;
  /** HOME: the "Right now" slip online (an invite, the job you're on; null = the book's own choice). */
  now(): BookNow | null;
  /** HOME: extra tile groups (the street panels). */
  groups(): BookTileGroup[];
  /** HOME: the live line on one of the book's own tiles, online (null = the book's own). */
  status(tab: BookTab): Pick<BookTile, 'status' | 'badge' | 'alert'> | null;
  /** Extra entries at the top of JOBS / CONTACTS. */
  entries(tab: 'jobs' | 'contacts'): BookEntry[];
  /** Run a message's panel action. */
  openPanel(id: string, arg?: unknown): boolean;
  /** Extra lines and buttons on a contact's page online (Lenny's list on Lenny's, WP-X8); null = nothing. */
  contactExtra?(id: string): { body: string[]; actions: BookButton[] } | null;
}

/** A section of the book another module draws (`addSection`). */
export interface BookSection {
  visible(): boolean;
  page(): BookPage;
  status(): Pick<BookTile, 'status' | 'badge' | 'alert'>;
}

export interface BookService {
  readonly isOpen: boolean;
  /** 'home' (the index page) or the section showing; null when shut. */
  readonly tab: BookView | null;
  /** Open on a section or HOME (the default: I, the BOOK knob, the pad). False if the screen is busy. */
  open(tab?: BookView): boolean;
  close(): void;
  toggle(tab?: BookView): void;
  /** Add a message to the inbox in play right now (offline or online). Null if a duplicate key. */
  post(m: PostInput): BookMessage | null;
  readonly unread: number;
  setOnlineProvider(p: OnlineBookProvider | null): void;
  /**
   * Extra offline JOBS entries after the job list (src/activities: Halloran's workshop, the matchbooks).
   * Asked every time the page is drawn; returns an unregister function.
   */
  addJobsSource(source: () => BookEntry[]): () => void;
  /**
   * A whole section another module owns (offline: Wilf's book, src/trade): its tile on HOME after YOUR RECORD
   * and its page, while `visible()`. Returns an unregister function.
   */
  addSection(tab: BookTab, section: BookSection): () => void;
  /** Redraw the open book (online state changed). */
  refresh(): void;
  act(a: MessageAction): void;
}

declare module '../core/services' {
  interface ServiceMap {
    book: BookService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Open the little black book (optionally on a section, or 'home'). */
    bookOpen(tab?: string): boolean;
    bookClose(): void;
    /** Book read-out: open, tab ('home' or the section), tabs, HOME's tiles and slip, entries on the page, unread, inbox, bleeper. */
    bookState(): Record<string, unknown>;
    /** Post a message to the inbox (tests / console). */
    bookPost(from: string, subject: string, text: string, kind?: string): boolean;
  }
}

const STORE_KEY = 'bh.book.v1';
const BLEEP_MS = 150;
/** Messages landing within this window after one another count as one delivery (one bleep, one slip). */
const BURST_MS = 1200;
/**
 * The rest of a delivery follow one slip at a time, this far apart and only while the HUD is free (not under a
 * result sheet or the book), so the side activities a finished job opens are each read, not folded into "plus 5 more".
 */
const TRAIL_MS = 4500;
/** Slips after the headline at most; anything beyond is counted on the last one. */
const TRAIL_MAX = 5;
/** Which note headlines a delivery of several: the law first, then the story, a call, work going, advice. */
const HEADLINE: Record<BookMessage['kind'], number> = { warning: 0, story: 1, call: 2, offer: 3, tip: 4, crew: 5, gang: 6, chat: 7, system: 8 };

/** The ruled headings on the JOBS and MESSAGES pages. */
const SECTION = {
  now: 'Right now',
  work: 'Work going',
  side: 'Between jobs',
  goal: 'Saving for',
  later: 'Later',
  done: 'Done',
  unread: 'New',
  read: 'Read',
  contacts: 'Your people',
  faces: 'Know them on sight',
} as const;

/** “Subject” — unless the subject already opens with a quote mark. */
const quoted = (s: string): string => (/^[“"']/.test(s) ? s : `“${s}”`);
/** `· 115 m` glued together, so a margin note that wraps on a phone never leaves the dot or the unit on its own. */
const dist = (m: number): string => `\u00a0·\u00a0${formatDistance(m).replace(' ', '\u00a0')}`;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

const pounds = (n: number): string => `£${formatMoney(Math.round(n))}`;

export function createBookSystem(): System {
  let ctx: GameContext;
  const offline = new Inbox();
  const online = new Inbox();
  let screen: BookScreen | null = null;
  let provider: OnlineBookProvider | null = null;
  const jobSources: (() => BookEntry[])[] = [];
  /** Sections other modules own (`addSection`): Wilf's book. */
  const sections = new Map<BookTab, BookSection>();
  let layer: HTMLElement | null = null;
  let bleeper: HTMLElement | null = null;
  let bleepCount: HTMLElement | null = null;
  let knobBadge: HTMLElement | null = null;
  let lastUnread = -1;
  let frozeTime = false;
  let padPrev = false;
  let bleeps = 0;
  let burst: BookMessage[] = [];
  let burstTimer = 0;
  let trail: BookMessage[] = [];
  let trailTimer = 0;
  let trailShown = 0;
  const offs: (() => void)[] = [];
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;

  const isOnline = (): boolean => !!ctx.services.tryGet('net')?.online;
  const inbox = (): Inbox => (isOnline() ? online : offline);
  const ui = () => ctx.services.tryGet('ui');
  const sfx = (s: 'tick' | 'click' | 'confirm' | 'back' | 'whoosh' | 'denied'): void => ctx.services.tryGet('audio')?.ui(s);

  function persist(): void {
    try {
      storage()?.setItem(STORE_KEY, JSON.stringify(offline.serialize()));
    } catch {
      /* full / blocked storage: the inbox just isn't kept */
    }
  }

  function load(): void {
    try {
      const raw = storage()?.getItem(STORE_KEY);
      if (raw) offline.restore(JSON.parse(raw));
    } catch {
      offline.clear();
    }
  }

  // ------------------------------------------------------------------ open / close

  function viewFor(tab: BookView): BookView {
    return tab === 'home' ? 'home' : tabFor(tab);
  }

  function open(tab?: BookView): boolean {
    const u = ui();
    if (!u || !ctx.uiRoot) return false;
    if (screen) {
      if (tab) {
        const v = viewFor(tab);
        if (v === 'home') screen.goHome();
        else screen.show(v, true);
      }
      return true;
    }
    const first = tab ? viewFor(tab) : 'home';
    const root = ctx.uiRoot.querySelector('.bh-ui') as HTMLElement | null;
    if (!root) return false;
    const s = new BookScreen(root, pages, first);
    if (!u.openOverlay(s, 'book', () => teardown(s))) {
      s.dispose();
      return false;
    }
    screen = s;
    // The touch controls hide with the next frame; don't let them sit over the book until then.
    ctx.uiRoot.classList.add('bk-open');
    sfx('whoosh');
    // Offline the world waits while you read; online it doesn't (the city is shared).
    if (!isOnline() && ctx.loop.timeScale > 0) {
      frozeTime = true;
      ctx.loop.timeScale = 0;
    }
    ctx.services.tryGet('player')?.controls.acquire('book');
    ctx.input.clearTouch?.();
    ctx.events.emit('bookOpened', { tab: s.view });
    return true;
  }

  /** Undo what opening did (also when the UI forces the book shut for a result sheet / pause). */
  function teardown(s: BookScreen): void {
    if (screen !== s) return;
    screen = null;
    s.dispose();
    ctx.uiRoot?.classList.remove('bk-open');
    if (frozeTime) {
      frozeTime = false;
      if (ctx.loop.timeScale === 0) ctx.loop.timeScale = 1;
    }
    ctx.services.tryGet('player')?.controls.release('book');
  }

  function close(): void {
    const s = screen;
    if (!s) return;
    ui()?.closeOverlay(s);
    teardown(s);
  }

  // ------------------------------------------------------------------ pages

  function mark(w: { x: number; z: number; label: string }): void {
    ctx.services.tryGet('onboarding')?.setWaypoint(w);
    ctx.services.tryGet('ui')?.toast(`Route set: ${w.label}. Follow the arrows on the road.`, 'info', 2.6);
    close();
  }

  function act(a: MessageAction): void {
    if (a.mark) mark(a.mark);
    else if (a.panel) {
      close();
      provider?.openPanel(a.panel, a.panelArg);
    } else if (a.tab) {
      if (screen) screen.show(tabFor(a.tab as BookTab), true);
      else open(a.tab as BookTab);
    }
  }

  function board(): JobBoardEntry[] {
    return ctx.services.tryGet('missions')?.board() ?? [];
  }

  /** Metres from the player to a point (for "· 120 m" in the margin and for sorting what is nearest). */
  function distanceTo(x: number, z: number): number | null {
    const p = ctx.services.tryGet('player')?.pos;
    return p ? Math.hypot(x - p.x, z - p.z) : null;
  }

  /** "Ring for a minicab" to a job's giver, when it's far, you're not wanted and you can pay (src/missions/minicab.ts). */
  function cabButton(j: JobBoardEntry, where: string): BookButton[] {
    const missions = ctx.services.tryGet('missions');
    if (!missions?.minicabOffered(j.id)) return [];
    return [
      {
        label: `Ring for a minicab — ${fareText(minicabFare(ctx))}`,
        sub: `to ${where}`,
        run: () => {
          close();
          const why = missions.minicabTo(j.id);
          if (why) ctx.services.tryGet('ui')?.toast(why, 'warn', 3);
        },
      },
    ];
  }

  /**
   * JOBS, in the order that matters now: what you are on (or what to do next), then the work that is
   * open — the next story job first, then the paid repeatables and the side activities nearest first —
   * then the thing you are saving for, then what is locked for later, and what is done at the bottom.
   */
  function jobsPage(): BookPage {
    const entries: BookEntry[] = [];
    const missions = ctx.services.tryGet('missions');
    const jobs = board();
    const active = missions?.active ? jobs.find((j) => j.id === missions.active) : undefined;
    /** The job the "what next" line on top already is (left out of Work going). */
    let nextJob: string | null = null;
    if (active) {
      entries.push({
        id: 'now',
        section: SECTION.now,
        title: `On the job: ${active.title}`,
        sub: active.client,
        mark: 'active',
        kicker: 'Right now',
        heading: active.title,
        body: [active.blurb, 'Follow the objective note (top left) and the red marker. The job tells you each step.'],
      });
    } else {
      const next = ctx.services.tryGet('onboarding')?.next() ?? null;
      if (next) {
        const t = next.target;
        const d = t ? distanceTo(t.x, t.z) : null;
        // The "what next" line usually is a job on the board (Borrowed Time, the ringing phone box): it is
        // that job, so it carries the job's page too and the job is not listed again under Work going
        // with a NEW stamp (it read as two things to do).
        const job = next.jobId ? jobs.find((j) => j.id === next.jobId && j.available) : undefined;
        if (job) nextJob = job.id;
        const jobKicker = job ? (job.kicker ?? (job.kind === 'export' ? 'Export order' : job.kind === 'street' ? 'Street job' : job.tag)) : '';
        entries.push({
          id: 'next',
          section: SECTION.now,
          title: next.text,
          sub: d !== null ? `${next.title}${dist(d)}` : next.title,
          mark: 'here',
          kicker: job ? `What next · ${jobKicker}` : 'What next',
          heading: next.title,
          body: [next.text, ...(next.hint ? [touchWords(next.hint)] : []), ...(job ? [job.blurb] : [])],
          ...(job ? { meta: touchWords(`${job.giver.prompt} at ${job.giver.name.replace(/\s*\([^)]*\)\s*$/, '')}. ${job.meta ?? `Pays ${pounds(job.reward)}.`}`) } : {}),
          actions: t ? [{ label: 'Mark on the map', sub: t.label, run: () => mark(t) }, ...(job ? cabButton(job, t.label) : [])] : [],
        });
      }
    }
    if (isOnline() && provider) entries.push(...provider.entries('jobs'));
    const lineFor = (j: JobBoardEntry, section: string): BookEntry => {
      const where = j.giver.name.replace(/\s*\([^)]*\)\s*$/, '');
      const waits = j.waitsFor.map((id) => jobs.find((x) => x.id === id)?.title ?? id);
      const target = { x: j.giver.x, z: j.giver.z, label: `${j.client.split(' ')[0]} — ${where}` };
      const d = j.available ? distanceTo(j.giver.x, j.giver.z) : null;
      return {
        id: `job:${j.id}`,
        section,
        title: j.title,
        sub: `${j.client} · ${where}${d !== null ? dist(d) : ''}`,
        mark: j.id === active?.id ? 'active' : j.available ? (j.kind === 'activity' && j.completed ? undefined : 'new') : j.completed ? 'done' : 'locked',
        kicker: j.kicker ?? (j.kind === 'export' ? 'Export order' : j.kind === 'street' ? 'Street job' : j.tag),
        heading: j.title,
        body: j.available || j.completed ? [j.blurb] : [`Not yet. Finish ${waits.map((w) => `“${w}”`).join(' and ')} first.`],
        meta: j.available ? touchWords(`${j.giver.prompt} at ${where}. ${j.meta ?? `Pays ${pounds(j.reward)}.`}`) : j.completed && j.kind === 'story' ? 'Done and paid.' : undefined,
        actions: j.available ? [{ label: 'Mark on the map', sub: where, run: () => mark(target) }, ...cabButton(j, where)] : [],
      };
    };
    /** A finished story job: its best stamp in the margin, what each stamp takes, and "Play it again". */
    const doneLine = (j: JobBoardEntry): BookEntry => {
      const rec = j.record ?? { medal: 1 as const, best: null, runs: 1 };
      const tier = (rec.medal || 1) as 1 | 2 | 3;
      const rule = MEDAL_RULES[j.id];
      const best = rec.best !== null ? ` · ${formatRunTime(rec.best)}` : '';
      const blocked = ctx.services.tryGet('missions')?.replayBlocked(j.id) ?? null;
      return {
        id: `job:${j.id}`,
        section: SECTION.done,
        title: j.title,
        sub: `${MEDAL_NAME[tier]}${best}`,
        mark: tier === 3 ? 'proper' : tier === 2 ? 'clean' : 'copper',
        kicker: `${j.tag} · ${MEDAL_STAMP[tier]}`,
        heading: j.title,
        body: [
          `Your best: ${MEDAL_NAME[tier]}${rec.best !== null ? `, in ${formatRunTime(rec.best)}` : ''}.`,
          ...(rule ? [`Done — finish the job.`, `Done clean — ${rule.clean}`, `Done proper — ${rule.proper}`] : []),
          'Play it again for a better stamp or time. A replay pays only the bonus — the main fee was paid the first time — and the story stays where it is.',
          ...(blocked ? [blocked] : []),
        ],
        meta: j.blurb,
        actions: blocked
          ? []
          : [
              {
                label: 'Play it again',
                sub: 'bonus only',
                run: () => {
                  close();
                  const why = ctx.services.tryGet('missions')?.replay(j.id);
                  if (why) ctx.services.tryGet('ui')?.toast(why, 'warn', 3);
                },
              },
            ],
      };
    };
    if (isOnline()) {
      entries.push({
        id: 'story-offline',
        section: SECTION.later,
        title: 'Mags’ story jobs',
        sub: 'offline only',
        mark: 'offline',
        heading: 'Story jobs are offline',
        body: ['Mags, Halloran’s export orders and the rest of the story are played offline. Online, work comes off the job boards.'],
      });
    } else {
      const byDistance = (a: JobBoardEntry, b: JobBoardEntry): number => (distanceTo(a.giver.x, a.giver.z) ?? 0) - (distanceTo(b.giver.x, b.giver.z) ?? 0);
      const open = jobs.filter((j) => j.available && j.id !== active?.id && j.id !== nextJob);
      // Paid work: the next story job, then export orders and street jobs, nearest first.
      for (const j of open.filter((j) => j.kind === 'story')) entries.push(lineFor(j, SECTION.work));
      for (const j of open.filter((j) => j.kind === 'export' || j.kind === 'street').sort(byDistance)) entries.push(lineFor(j, SECTION.work));
      // Between jobs: races and shifts nearest first, then the workshop and the matchbooks.
      for (const j of open.filter((j) => j.kind === 'activity').sort(byDistance)) entries.push(lineFor(j, SECTION.side));
      for (const src of jobSources) for (const e of src()) entries.push({ section: SECTION.side, ...e });
      const goal = lockupGoal();
      if (goal) entries.push(goal);
      // Locked story jobs in story order, then the finished ones.
      for (const j of jobs.filter((j) => j.kind === 'story' && !j.available && !j.completed && j.id !== active?.id)) entries.push(lineFor(j, SECTION.later));
      for (const j of jobs.filter((j) => j.kind === 'story' && j.completed && !j.available && j.id !== active?.id)) entries.push(doneLine(j));
    }
    return { heading: 'Jobs', lede: isOnline() ? 'Work in the shared city.' : 'Who wants what, and what to do next.', entries };
  }

  /** The lockup key while you don't have it: something to save for (JOBS, MONEY and YOUR MOTOR). */
  function lockupGoal(section: string = SECTION.goal): BookEntry | null {
    const save = ctx.services.tryGet('save');
    if (!save || save.hasUnlock(LOCKUP_UNLOCK_ID)) return null;
    const money = cash();
    const lockup = playerLockupPoi(ctx.services.tryGet('layout')?.pois ?? []);
    return {
      id: 'goal:lockup',
      section,
      title: `Lockup key — ${pounds(LOCKUP_PRICE)}`,
      sub: money >= LOCKUP_PRICE ? 'you can afford it' : `you have ${pounds(money)}`,
      mark: money >= LOCKUP_PRICE ? 'new' : 'locked',
      kicker: 'Something to save for',
      heading: 'A lockup of your own',
      body: [
        `${lockup?.name ?? 'The lockup'}: a garage under the arches with a Vesper GT in it. Wreck it and another turns up.`,
        money >= LOCKUP_PRICE ? 'You have the money. Press E at the lockup door.' : `Another ${pounds(LOCKUP_PRICE - money)} and it’s yours.`,
      ].map(touchWords),
      actions: lockup ? [{ label: 'Mark on the map', sub: lockup.name, run: () => mark({ x: lockup.pos.x, z: lockup.pos.z, label: lockup.name }) }] : [],
    };
  }

  function cash(): number {
    return ctx.services.tryGet('economy')?.balance ?? ctx.state.money;
  }

  /** A contact's place as a "Mark on the map" button (Sid's, Halloran's, Pike's). */
  function contactMark(id: string): BookButton[] {
    const c = CONTACTS.find((x) => x.id === id);
    const pos = c ? contactPos(ctx, c) : null;
    return c && pos ? [{ label: 'Mark on the map', sub: c.where, run: () => mark({ x: pos.x, z: pos.z, label: `${c.short} — ${c.where}` }) }] : [];
  }

  /** MONEY offline: what's on you, what to save for, where it goes. */
  function moneyPage(): BookPage {
    const money = cash();
    const entries: BookEntry[] = [
      {
        id: 'money:pocket',
        section: 'On you',
        title: `${pounds(money)} in your pocket`,
        sub: 'every job pays cash',
        kicker: 'Your money',
        heading: pounds(money),
        body: ['Every job pays cash into your pocket: Mags’ jobs, Halloran’s export orders, the races, the minicab shifts, Lenny’s matchbooks.', 'Online, the shared city has a bank too.'],
      },
    ];
    const goal = lockupGoal('Saving for');
    if (goal) entries.push(goal);
    entries.push(
      {
        id: 'money:sid',
        section: 'Where it goes',
        title: 'Sid the fence — guns and rounds',
        sub: 'Gorringe’s back room',
        kicker: 'Spend it',
        heading: 'Sid Marlowe',
        avatar: 'Sid Marlowe',
        body: ['Coshes, bats, a revolver, a sawn-off and rounds, in the back room at Gorringe’s.'],
        actions: contactMark('sid'),
      },
      {
        id: 'money:workshop',
        section: 'Where it goes',
        title: 'Des’s workshop — tuning',
        sub: 'at Halloran’s',
        kicker: 'Spend it',
        heading: 'Des’s workshop',
        avatar: 'Des Halloran',
        body: ['Engine, brakes, grip and paint for the motor you drive in.'],
        actions: contactMark('halloran'),
      },
      {
        id: 'money:pike',
        section: 'Where it goes',
        title: 'Pike’s — paint and a clean slate',
        sub: 'respray and the spray bay',
        kicker: 'Spend it',
        heading: 'Pike’s Respray',
        avatar: 'Pike’s Respray',
        body: ['A new coat makes a hot car cold again; the spray bay does paint, rims, neon and stance on a car you keep.'],
        actions: contactMark('pike'),
      },
    );
    return { heading: 'Money', lede: 'What you have and where it goes.', entries };
  }

  /** The motor you are sitting in, as one line ("Vesper GT · 82 %"); null on foot. */
  function motorLine(): { name: string; pct: number } | null {
    const v = ctx.services.tryGet('player')?.vehicle;
    if (!v || v.destroyed) return null;
    return { name: v.spec.displayName, pct: Math.max(0, Math.round((v.health / Math.max(1, v.spec.maxHealth)) * 100)) };
  }

  /** YOUR MOTOR offline: what you drive, the lockup, the places that look after a car. */
  function motorPage(): BookPage {
    const m = motorLine();
    const v = ctx.services.tryGet('player')?.vehicle;
    const entries: BookEntry[] = [
      m
        ? {
            id: 'motor:now',
            section: 'Now',
            title: m.name,
            sub: `condition ${m.pct} %${v?.stolen ? ' · stolen' : ''}`,
            mark: 'active',
            kicker: 'You’re driving',
            heading: m.name,
            body: [`Condition ${m.pct} %. ${m.pct < 35 ? 'She won’t take much more: smoke first, then fire.' : 'Keep it off the kerbs.'}`, ...(v?.stolen ? ['The police may know this one. Pike’s repaints it.'] : [])],
          }
        : {
            id: 'motor:now',
            section: 'Now',
            title: 'On foot',
            sub: 'any parked car will do',
            kicker: 'You’re walking',
            heading: 'On foot',
            body: [touchWords('Walk up to a car and press E to get in. Taking one that isn’t yours is a crime if anyone sees.')],
          },
    ];
    const save = ctx.services.tryGet('save');
    const lockup = playerLockupPoi(ctx.services.tryGet('layout')?.pois ?? []);
    if (save?.hasUnlock(LOCKUP_UNLOCK_ID) && lockup) {
      entries.push({
        id: 'motor:lockup',
        section: 'Your places',
        title: 'Your lockup — the Vesper GT',
        sub: lockup.name,
        kicker: 'Yours',
        heading: lockup.name,
        body: ['Your own Vesper GT under the arches. Wreck it and another turns up.'],
        actions: [{ label: 'Mark on the map', sub: lockup.name, run: () => mark({ x: lockup.pos.x, z: lockup.pos.z, label: lockup.name }) }],
      });
    } else {
      const goal = lockupGoal('Your places');
      if (goal) entries.push(goal);
    }
    entries.push(
      {
        id: 'motor:pike',
        section: 'Looking after it',
        title: 'Pike’s Respray',
        sub: 'paint, rims, neon, a clean slate',
        kicker: 'Garage',
        heading: 'Pike’s Respray',
        avatar: 'Pike’s Respray',
        body: ['Drive a hot motor in and stop: new paint, dents knocked out, and the law no longer knows it. The spray bay does paint, rims, neon and stance on a car you keep.'],
        actions: contactMark('pike'),
      },
      {
        id: 'motor:workshop',
        section: 'Looking after it',
        title: 'Des’s workshop',
        sub: 'engine, brakes, grip',
        kicker: 'Garage',
        heading: 'Des’s workshop',
        avatar: 'Des Halloran',
        body: ['Tuning and paint for the motor you drive in, at Halloran’s.'],
        actions: contactMark('halloran'),
      },
    );
    return { heading: 'Your motor', lede: 'What you drive and who looks after it.', entries };
  }

  function messagesPage(): BookPage {
    const now = Date.now();
    const box = inbox();
    // Unread mail under its own heading while there is any; once it's all read the headings go.
    const grouped = box.unread > 0;
    const entries: BookEntry[] = box.list().map((m) => ({
      id: m.id,
      ...(grouped ? { section: m.read ? SECTION.read : SECTION.unread } : {}),
      title: m.subject,
      sub: `${m.from} · ${agoText(m.at, now)}`,
      mark: m.read ? undefined : 'new',
      avatar: m.from,
      photo: { name: m.from, size: 'note' as const },
      kicker: KIND_KICKER[m.kind],
      heading: m.subject,
      body: [`From ${m.from}`, ...m.text.split('\n\n').map(touchWords)],
      // A map mark is labelled by the deed with the place as the small line, as on JOBS ("Mark on the map ·
      // Mags — The Crooked Anchor"), not "Mark The Crooked Anchor on the map".
      actions: m.action ? [m.action.mark ? { label: 'Mark on the map', sub: m.action.mark.label, run: () => act(m.action!) } : { label: m.action.label, run: () => act(m.action!) }] : [],
      onSelect: () => {
        if (box.markRead(m.id)) {
          if (box === offline) persist();
          updateBleeper(true);
        }
      },
    }));
    return {
      heading: 'Messages',
      lede: box.unread ? `${box.unread} not read yet.` : 'Notes, calls and word on the street.',
      entries,
      empty: 'Nobody has been in touch yet.',
    };
  }

  function contactsPage(): BookPage {
    const entries: BookEntry[] = [];
    if (isOnline() && provider) entries.push(...provider.entries('contacts'));
    const box = inbox();
    const jobs = board();
    // Offline, the faces the story has introduced so far go under their own heading after the contacts.
    const save = ctx.services.tryGet('save');
    const faces = isOnline() || !save ? [] : FACES.filter((f) => save.isMissionComplete(f.after));
    for (const c of CONTACTS) {
      // The side-activity people only go in the book once they've been in touch or have work going.
      // Online, a contact with a ladder in the city (Lenny's jobs) is always listed: the ladder is the introduction.
      if (c.introduced && !(isOnline() && ladderOf(c.id)) && !box.list().some((m) => m.from === c.name) && !jobs.some((j) => j.client === c.name && (j.available || j.completed))) continue;
      const pos = contactPos(ctx, c);
      const last = box.list().find((m) => m.from === c.name);
      const d = pos ? distanceTo(pos.x, pos.z) : null;
      const ladder = ladderOf(c.id);
      const extra = isOnline() ? (provider?.contactExtra?.(c.id) ?? null) : null;
      entries.push({
        id: `contact:${c.id}`,
        ...(isOnline() ? { section: 'The old faces' } : {}),
        ...(faces.length ? { section: SECTION.contacts } : {}),
        title: c.name,
        sub: `${ladder && ladder.rung > 0 ? `${standingName(ladder.track, ladder.rung)} · ` : ''}${d !== null ? `${c.where}${dist(d)}` : c.where}`,
        ...(ladder ? { ladder } : {}),
        avatar: c.name,
        photo: { name: c.name, size: 'large' as const },
        icon: c.icon,
        kicker: c.role,
        heading: c.name,
        body: [touchWords(c.about), ...(last ? [`Last word: ${quoted(last.subject)} (${agoText(last.at, Date.now())}).`] : []), ...(extra?.body ?? [])],
        actions: [
          ...(extra?.actions ?? []),
          ...(pos ? [{ label: 'Mark on the map', sub: c.where, run: () => mark({ x: pos.x, z: pos.z, label: `${c.short} — ${c.where}` }) }] : []),
          ...(last ? [{ label: 'Read the last message', run: () => showMessage(last.id) }] : []),
        ],
      });
    }
    // Online, Wilf deals out of the arches (offline he is one of the faces): his ladder sits with the others.
    const wilf = isOnline() ? ladderOf('wilf') : null;
    const wilfFace = FACES.find((f) => f.id === 'wilf');
    if (wilf && wilfFace) {
      entries.push({
        id: 'contact:wilf',
        section: 'The old faces',
        title: wilfFace.name,
        sub: `${wilf.rung > 0 ? `${standingName('wilf', wilf.rung)} · ` : ''}${wilfFace.role}`,
        avatar: wilfFace.name,
        photo: { name: wilfFace.name, size: 'large' },
        kicker: 'The fence',
        icon: 'scales',
        heading: wilfFace.name,
        ladder: wilf,
        body: ['Fences out of the railway arches in Cinder Yards, and knows every fence and supplier in the city. Trade with them and Wilf hears about it.'],
      });
    }
    // A voice on the line rather than an address (his portrait shows by name): the bent desk sergeant, offline.
    if (!isOnline()) {
      const bribe = (l: number): number => discounted(BRIBE_PRICE[l]!, contactPerk(ctx, 'sergeant'));
      entries.push({
        id: 'contact:sergeant',
        section: 'Useful numbers',
        title: SERGEANT,
        avatar: SERGEANT,
        sub: 'ring from any phone box',
        icon: 'helmet',
        kicker: 'The front desk at the nick',
        heading: SERGEANT,
        body: [
          'Keeps the front desk at Low Borough police station and, like DS Holloway, likes a drink. While you’re wanted, ring the desk from any phone box and a report goes missing: your wanted level drops by one.',
          `Price: £${bribe(1)} at wanted level 1, £${bribe(2)} at level 2, £${bribe(3)} at level 3${contactPerk(ctx, 'sergeant') > 0 ? ' (Mags’ word knocks a bit off)' : ''}. At level 4 nobody at the desk can help.`,
          'One favour an hour on the borough clock. Never in the middle of a job. From level 2, not while the police can see you — get out of their sight first.',
        ].map(touchWords),
      });
    }
    for (const f of faces) {
      entries.push({
        id: `face:${f.id}`,
        section: SECTION.faces,
        title: f.name,
        sub: f.role,
        avatar: f.name,
        photo: { name: f.name, size: 'large' },
        kicker: f.role,
        heading: f.name,
        body: [f.about],
      });
    }
    const known = contactsSvc() ? bestTitle(contactsSvc()!.standing(), contactsSvc()!.mode) : null;
    const lede = known ? `They know you as “${known.title}”.` : isOnline() ? 'Your crew and gang, then the old faces.' : 'Work for them and they look after you.';
    return { heading: 'Contacts', lede, entries };
  }

  const contactsSvc = () => ctx.services.tryGet('contacts');

  /** The ladder on a contact's page (WP-X1): by the book's contact id, in the mode being played. */
  function ladderOf(contactId: string): ReturnType<typeof ladderFor> | null {
    const svc = contactsSvc();
    if (!svc) return null;
    const track = tracksFor(svc.mode).find((t: ContactTrack) => TRACKS[t].page === contactId);
    return track ? ladderFor(track, svc.standing(), svc.mode, svc.todayUsed(track)) : null;
  }

  /** Turn to MESSAGES with one message chosen (and only that one marked read). */
  function showMessage(id: string): void {
    screen?.show('messages', true, id);
  }

  /**
   * Offline, GANG and CREW were two tabs that each said "Play online": one ONLINE tab now says what the
   * shared city has (a gang, a crew, the street) on one page, and how to get there. Online is unchanged.
   */
  function onlinePage(): BookPage {
    const what: [string, string, string, string][] = [
      ['gang', 'Gangs', 'a treasury, a stash, ranks', 'Start a gang or join one in the shared city: a treasury, a stash, ranks and colours on your motors.'],
      ['crew', 'Crews', 'up to five of you', 'Up to five of you working together for an evening, no gang needed: shared chat, your mates on the map, warnings, Night Freight runs.'],
      ['street', 'The street', 'pockets, chat, the panels', 'Pockets, your job, chat and the rest of the online panels.'],
    ];
    return {
      heading: 'Online',
      lede: 'Only in the shared city: quit to the title screen and pick Play online.',
      entries: what.map(([id, title, sub, body]) => ({
        id: `online:${id}`,
        title,
        sub,
        mark: 'offline' as const,
        kicker: 'Online only',
        heading: title,
        body: [body, 'Quit to the title screen and pick Play online.'],
      })),
    };
  }

  /** A tab asked for by name (a message's button, a hook): offline, GANG / CREW / STREET are the ONLINE page. */
  function tabFor(tab: BookTab): BookTab {
    if (isOnline()) return tab === 'online' ? 'gang' : tab;
    return tab === 'gang' || tab === 'crew' || tab === 'street' ? 'online' : tab;
  }

  function mapPage(): BookPage {
    const wp = ctx.services.tryGet('onboarding')?.waypoint ?? null;
    const entries: BookEntry[] = [
      {
        id: 'map:open',
        title: 'Unfold the full map',
        sub: touchUi.enabled ? 'or tap MAP' : 'or press M',
        heading: 'The borough',
        body: [
          'The red diamond is your job’s next stop — red arrows on the road lead there. A plum flag is a place you chose (Mark on the map, or tap it on the map and Route here): plum arrows lead there. A dashed ring is a suggestion; no arrows until you choose it.',
          'Green discs are people with work: M is Mags, ☎ a ringing phone box, £ Halloran’s export orders, ★ Rita’s races, C a minicab shift, W the workshop, H a half-brick. Paper discs are places: F Sid the fence, G a garage, R the respray shop, L a lockup, S the safehouse, ! the police station. Police are grey dots, flashing red and blue when they’re after you; orange triangles are trouble.',
        ],
        actions: [{ label: 'Open the map', key: 'M', run: () => openUiScreen('map') }],
      },
    ];
    if (wp) {
      entries.push({
        id: 'map:clear',
        title: `Clear your route: ${wp.label}`,
        heading: 'Your route',
        body: [`The arrows on the road lead to ${wp.label}. The route clears by itself when you get there.`],
        actions: [
          {
            label: 'Clear route',
            run: () => {
              ctx.services.tryGet('onboarding')?.setWaypoint(null);
              screen?.show('map', true);
            },
          },
        ],
      });
    }
    return { heading: 'Map', lede: 'Where everything is.', entries };
  }

  function helpPage(): BookPage {
    return {
      heading: 'How to play',
      lede: 'The game in a minute.',
      entries: [
        {
          id: 'help:loop',
          title: 'Read the guide',
          sub: 'controls, the law, online',
          heading: 'How it goes',
          body: HELP_LOOP.map((s, i) => `${i + 1}. ${s.head} — ${s.text}`),
          actions: [{ label: 'Read the full guide', run: () => openUiScreen('help') }],
        },
        ...(isOnline() ? ONLINE_HOWTO : OFFLINE_HOWTO).map((h) => ({ ...h, body: (h.body ?? []).map(touchWords) })),
      ],
    };
  }

  /** Short how-tos beyond the loop (the gameplay review found the page had one entry and left most systems unexplained). */
  const OFFLINE_HOWTO: BookEntry[] = [
    {
      id: 'help:side',
      title: 'Work between jobs',
      sub: 'exports, races, cabs, matchbooks',
      heading: 'Work between jobs',
      body: [
        'Halloran’s export orders (the key on the map): steal the motor he names and drive it to his garage. A new order comes up each time.',
        'Rita’s street races (the flag): pay the entry, win the pot. Dot’s minicab shifts (the cab): fares across the borough against the clock.',
        'Lenny pays for every matchbook you turn up — they glint in odd corners of the borough.',
      ],
    },
    {
      id: 'help:spend',
      title: 'What money buys',
      sub: 'the lockup, guns, the workshop',
      heading: 'What money buys',
      body: [
        'The lockup key (the lockup on the map): a car of your own, replaced if it’s wrecked.',
        'Sid the fence at Gorringe’s (the revolver): coshes, bats, a revolver, a sawn-off and rounds.',
        'Des’s workshop at Halloran’s (the cog): tuning and paint for the motor you drive in.',
      ],
    },
    {
      id: 'help:law',
      title: 'Shaking the police',
      sub: 'out of sight, Pike’s, the desk',
      heading: 'Shaking the police',
      body: [
        'Get out of their sight and keep going: your wanted level drops once they lose you.',
        'Pike’s Respray (the spray can) repaints a car the police know — drive in and stop.',
        `${SERGEANT} on the front desk makes a report go missing for a price: ring him from any phone box while you’re wanted.`,
      ],
    },
  ];
  const ONLINE_HOWTO: BookEntry[] = [
    {
      id: 'help:online-money',
      title: 'Making money in the city',
      sub: 'jobs, goods, the bank',
      heading: 'Making money in the city',
      body: [
        'Job boards at phone boxes, pubs and Halloran’s: courier and recovery work, steady and legal.',
        'Goods: buy from a supplier, sell across town where they fetch more. The pub’s word on the street has the prices. Each sale adds heat.',
        'Bank your cash: a fine or a hospital bill comes out of your pocket, never the bank.',
      ],
    },
    {
      id: 'help:online-crew',
      title: 'Playing with friends',
      sub: 'crews and Night Freight',
      heading: 'Crews and Night Freight',
      body: [
        'A crew is up to five of you for the evening, no gang needed: open the crew sheet and invite by name.',
        'Night Freight is the crew’s job: two or more of you at Bonded Warehouse No. 3, one driving a car with a boot. Crates cost £300 and fetch £650 at the drop — watch for police and rival crews on the way.',
      ],
    },
    {
      id: 'help:online-gang',
      title: 'Running a gang',
      sub: 'treasury, premises, turf',
      heading: 'Running a gang',
      body: [
        'A gang lasts: a shared treasury and stash, ranks, colours, premises it owns together, and districts it runs. The ledger’s Overview says what to do next.',
        'Reputation opens more members, stash room and premises. It comes from work done together: Night Freight, the gang business’s takings, and turf.',
      ],
    },
  ];

  function settingsPage(): BookPage {
    const tips = ctx.services.tryGet('onboarding')?.tipsSetting;
    const entries: BookEntry[] = [
      {
        id: 'settings:all',
        title: 'All settings',
        sub: 'sound · picture · driving · controls',
        heading: 'Settings',
        body: [
          'Sound: the volumes. Picture: graphics quality, time of day, camera zoom and shake. Driving: steering sensitivity, the assists and, on a phone, classic or point-where-to-go steering.',
          touchUi.enabled ? 'Touch controls: size, opacity, left-handed layout and vibration.' : 'Everything is saved as you change it.',
        ],
        actions: [{ label: 'Open settings', run: () => openUiScreen('settings') }],
      },
    ];
    if (tips) {
      entries.unshift({
        id: 'settings:tips',
        title: `Tips and pointers: ${tips.get() ? 'ON' : 'OFF'}`,
        sub: 'the little slips that explain things',
        heading: 'Tips and pointers',
        body: ['Short notes that show up when something new happens — the first car, the first time the law is after you — and go once you’ve done it.'],
        actions: [
          {
            label: tips.get() ? 'Turn tips off' : 'Turn tips on',
            run: () => {
              tips.set(!tips.get());
              screen?.refresh();
            },
          },
        ],
      });
    }
    return { heading: 'Settings', entries };
  }

  /** YOUR RECORD offline (recordPage.ts); online the feed draws it from the server's record. */
  function recordPage(): BookPage {
    const d = ctx.services.tryGet('save')?.data;
    return offlineRecordPage({
      stats: d?.stats ?? defaultStats(),
      completedMissions: d?.completedMissions ?? [],
      jobRecords: d?.jobRecords ?? {},
      activities: d?.activities ?? emptyActivities(),
      story: MISSIONS.map((m) => ({ id: m.id, title: m.title })),
    });
  }

  function openUiScreen(name: 'map' | 'help' | 'settings'): void {
    close();
    ui()?.showScreen(name);
  }

  /** Sections other modules own that are showing now (after YOUR RECORD). */
  function extraTabs(): BookTab[] {
    return [...sections].filter(([, s]) => s.visible()).map(([t]) => t);
  }

  function tabs(): BookTab[] {
    return ['jobs', 'messages', 'contacts', 'map', 'money', 'motor', 'record', ...extraTabs(), ...(isOnline() ? ([...(weekShown() ? (['week'] as const) : []), ...(hideoutShown() ? (['hideout'] as const) : []), 'gang', 'crew', 'street'] as const) : (['online'] as const)), 'help', 'settings'];
  }

  /** The hideout (WP-X4): online only, once you are in a gang (progressive disclosure: the provider says). */
  function hideoutShown(): boolean {
    return isOnline() && !!provider?.status('hideout');
  }

  /** The week (WP-X5) is online only, and only once the book shows it (progressive disclosure: the provider says). */
  function weekShown(): boolean {
    return isOnline() && !!provider?.status('week');
  }

  // ------------------------------------------------------------------ HOME

  /** The one thing that matters most right now, with its main button (offline; online asks the provider first). */
  function nowCard(): BookNow | null {
    if (isOnline() && provider) {
      const p = provider.now();
      if (p) return p;
    }
    const missions = ctx.services.tryGet('missions');
    const jobs = board();
    const active = !isOnline() && missions?.active ? jobs.find((j) => j.id === missions.active) : undefined;
    if (active) {
      return {
        kicker: 'On the job',
        title: active.title,
        sub: 'Follow the objective note (top left) and the red marker.',
        tone: 'job',
        avatar: active.client,
        tab: 'jobs',
        focusId: 'now',
        actions: [{ label: 'Back to it', run: close }],
      };
    }
    const next = isOnline() ? null : (ctx.services.tryGet('onboarding')?.next() ?? null);
    if (next) {
      const t = next.target;
      const d = t ? distanceTo(t.x, t.z) : null;
      const job = next.jobId ? jobs.find((j) => j.id === next.jobId && j.available) : undefined;
      return {
        kicker: 'What next',
        title: next.text,
        sub: d !== null ? `${next.title}${dist(d)}` : next.title,
        tone: 'next',
        ...(job ? { avatar: job.client } : {}),
        tab: 'jobs',
        focusId: 'next',
        actions: t ? [{ label: 'Mark on the map', sub: t.label, run: () => mark(t) }, ...(job ? cabButton(job, t.label) : [])] : [],
      };
    }
    const box = inbox();
    const unread = box.list().find((m) => !m.read);
    if (unread) {
      return {
        kicker: box.unread === 1 ? 'A new message' : `${box.unread} new messages`,
        title: unread.subject,
        sub: `From ${unread.from}`,
        tone: 'invite',
        avatar: unread.from,
        tab: 'messages',
        focusId: unread.id,
        actions: [{ label: 'Read it', run: () => screen?.openSection('messages', unread.id) }],
      };
    }
    return {
      kicker: 'Right now',
      title: 'Nothing pressing',
      sub: 'Find some work: the jobs going are in Jobs.',
      tone: 'calm',
      tab: 'jobs',
      actions: [{ label: 'See the jobs', run: () => screen?.openSection('jobs') }],
    };
  }

  /** The live line under each of the book's own tiles. */
  function tileStatus(tab: BookTab): Pick<BookTile, 'status' | 'badge' | 'alert'> {
    if (isOnline() && provider) {
      const p = provider.status(tab);
      if (p) return p;
    }
    const sec = sections.get(tab);
    if (sec) return sec.status();
    switch (tab) {
      case 'jobs': {
        const missions = ctx.services.tryGet('missions');
        const jobs = board();
        const active = missions?.active ? jobs.find((j) => j.id === missions.active) : undefined;
        if (active) return { status: `Job running: ${active.title}`, alert: true };
        const story = jobs.find((j) => j.kind === 'story' && j.available);
        if (story) return { status: `Next: ${story.title}` };
        const open = jobs.filter((j) => j.available).length;
        return { status: open ? `${open} going` : 'Nothing going yet' };
      }
      case 'messages': {
        const n = inbox().unread;
        return n ? { status: `${n} new`, badge: String(Math.min(99, n)), alert: true } : { status: inbox().list().length ? 'All read' : 'Nothing yet' };
      }
      case 'contacts': {
        const n = contactsPage().entries.length;
        return { status: `${n} ${n === 1 ? 'name' : 'names'}` };
      }
      case 'money':
        return { status: `${pounds(cash())} on you` };
      case 'motor': {
        const m = motorLine();
        return { status: m ? `${m.name} · ${m.pct} %` : 'On foot' };
      }
      case 'map': {
        const wp = ctx.services.tryGet('onboarding')?.waypoint ?? null;
        return { status: wp ? `Route: ${wp.label}` : 'Where it all is' };
      }
      case 'record': {
        const st = ctx.services.tryGet('save')?.data.stats;
        return { status: st ? `${Math.floor(st.playTime / 3600) ? `${Math.floor(st.playTime / 3600)} h ` : ''}${Math.floor((st.playTime % 3600) / 60)} min on the manor` : 'What you’ve done' };
      }
      case 'online':
        return { status: 'Gangs, crews' };
      case 'help':
        return { status: 'A quick guide' };
      case 'settings': {
        const tips = ctx.services.tryGet('onboarding')?.tipsSetting;
        return { status: tips ? `Tips ${tips.get() ? 'on' : 'off'}` : 'Sound, picture, controls' };
      }
      default:
        return {};
    }
  }

  function homePage(): BookHome {
    const week = weekShown() ? (['week'] as const) : [];
    const hide = hideoutShown() ? (['hideout'] as const) : [];
    const own: BookTab[] = ['jobs', 'messages', 'contacts', 'map', 'money', 'motor', 'record', ...extraTabs(), ...week, ...hide, ...(isOnline() ? [] : (['online'] as const)), 'help', 'settings'];
    const tiles: BookTile[] = own.map((t) => ({ id: t, tab: t, label: TAB_LABELS[t], icon: TAB_ICONS[t], ...tileStatus(t) }));
    // The profile page (src/profile) is a picture, not a page of the book: its tile after YOUR RECORD opens it straight.
    const profile = ctx.services.tryGet('profile');
    if (profile) {
      tiles.splice(tiles.findIndex((t) => t.id === 'record') + 1, 0, {
        id: 'profile',
        label: 'Profile',
        icon: 'profile',
        status: profile.status(),
        run: () => {
          close();
          profile.open();
        },
      });
    }
    const groups: BookTileGroup[] = [{ id: 'book', tiles }];
    if (isOnline() && provider) groups.push(...provider.groups());
    return { now: nowCard(), groups };
  }

  const pages: BookPages = {
    tabs,
    home: homePage,
    page(tab) {
      if (isOnline() && provider) {
        const p = provider.page(tab);
        if (p) return p;
      }
      const sec = sections.get(tab);
      if (sec) return sec.page();
      switch (tab) {
        case 'jobs':
          return jobsPage();
        case 'messages':
          return messagesPage();
        case 'contacts':
          return contactsPage();
        case 'gang':
        case 'crew':
        case 'street':
        case 'online':
        case 'week':
        case 'hideout':
          return onlinePage();
        case 'money':
          return moneyPage();
        case 'motor':
          return motorPage();
        case 'record':
          return recordPage();
        case 'map':
          return mapPage();
        case 'help':
          return helpPage();
        case 'settings':
          return settingsPage();
        default:
          return { heading: TAB_LABELS[tab], entries: [] };
      }
    },
    unread: () => inbox().unread,
    touch: () => touchUi.enabled,
    sound: sfx,
    close,
  };

  // ------------------------------------------------------------------ bleeper

  function post(m: PostInput): BookMessage | null {
    const box = inbox();
    const msg = box.post(m);
    if (!msg) return null;
    if (box === offline) persist();
    if (!msg.read && !m.quiet) announce(msg);
    updateBleeper(true);
    screen?.refresh();
    return msg;
  }

  /**
   * One bleep per delivery, not per message: a finished job brings four or five notes at once (the client,
   * Halloran, Sid, Rita, Dot). Whatever lands within a moment of the first is one delivery: the pager goes
   * off once and one slip names the note that matters most (the law, then the story, a call, work going,
   * advice) with the rest counted — "Bleep! Mags Delaney: “Borrowed Time” — settled. 5 more to follow.
   * Press I to read them." The rest then follow one slip at a time, a few seconds apart (`TRAIL_MS`).
   */
  function announce(msg: BookMessage): void {
    burst.push(msg);
    if (!burstTimer) burstTimer = window.setTimeout(deliver, BURST_MS);
  }

  function deliver(): void {
    burstTimer = 0;
    // Anything read in the meantime (the book was open on MESSAGES) has been seen already.
    const notes = burst.filter((m) => !m.read);
    burst = [];
    if (notes.length === 0) return;
    if (screen?.view === 'messages') return; // already reading the page: the list redraws itself
    bleeps++;
    buzz();
    // Most important first; a stable sort keeps the order they were posted in within a kind.
    const ordered = [...notes].sort((a, b) => HEADLINE[a.kind] - HEADLINE[b.kind]);
    const head = ordered[0]!;
    const rest = ordered.slice(1);
    if (rest.length === 0) {
      ui()?.toast(touchWords(`Bleep! ${head.from}: ${head.subject}. Press I to read it.`), 'info', 4, 'nudge');
      return;
    }
    ui()?.toast(touchWords(`Bleep! ${head.from}: ${head.subject}. ${rest.length} more to follow. Press I to read them.`), 'info', 4, 'nudge');
    if (trail.length === 0) trailShown = 0;
    trail.push(...rest);
    scheduleTrail(TRAIL_MS);
  }

  function scheduleTrail(ms: number): void {
    if (!trailTimer && trail.length > 0) trailTimer = window.setTimeout(nextTrail, ms);
  }

  /** The next slip of a delivery: skipped once read, held while a result sheet, the book or a menu is up. The
   *  slips are nudges (the notes are in the book): while wanted the UI drops them, the pager still counts. */
  function nextTrail(): void {
    trailTimer = 0;
    trail = trail.filter((m) => !m.read);
    if (trail.length === 0) return;
    // and in a chase, a fight or at speed (src/ui/attention.ts): the rest of the post waits for a quiet moment
    if (!ui()?.free || ui()?.attention === 'action') return scheduleTrail(1000);
    const m = trail.shift()!;
    trailShown++;
    const last = trailShown >= TRAIL_MAX && trail.length > 0;
    const more = last ? ` Plus ${trail.length} more in your book.` : '';
    if (last) trail = [];
    buzz(true);
    ui()?.toast(touchWords(`Bleep! ${m.from}: ${m.subject}.${more} Press I to read ${more ? 'them' : 'it'}.`), 'info', 4, 'nudge');
    scheduleTrail(TRAIL_MS);
  }

  /** The pager goes off: the slip (desktop) or the BOOK knob (touch) shakes, three ticks, a buzz in the hand (`soft`: one tick, no buzz). */
  function buzz(soft = false): void {
    for (const e of [bleeper, knobBadge?.parentElement ?? null]) {
      if (!e) continue;
      e.classList.remove('buzz');
      void e.offsetWidth;
      e.classList.add('buzz');
    }
    const audio = ctx.services.tryGet('audio');
    if (audio && soft) audio.ui('tick');
    if (soft) return;
    if (audio) {
      audio.ui('tick');
      window.setTimeout(() => audio.ui('tick'), BLEEP_MS);
      window.setTimeout(() => audio.ui('tick'), BLEEP_MS * 2);
    }
    if (touchUi.enabled && ctx.services.tryGet('save')?.settings.touchHaptics !== false) haptic([40, 60, 40]);
  }

  /** The count on the pager / the BOOK knob's badge. The bleep itself comes with the delivery (announce). */
  function updateBleeper(force = false): void {
    const n = inbox().unread;
    if (n === lastUnread && !force) return;
    lastUnread = n;
    const text = n > 0 ? String(Math.min(99, n)) : '';
    if (bleepCount) setText(bleepCount, n > 0 ? String(Math.min(99, n)).padStart(2, '0') : '--');
    if (bleeper) toggle(bleeper, 'unread', n > 0);
    if (knobBadge) {
      setText(knobBadge, text);
      knobBadge.hidden = n === 0;
    }
  }

  function buildHud(root: HTMLElement): void {
    layer = el('div', 'bh-ui bk-layer', undefined, root);
    layer.style.pointerEvents = 'none';
    const b = el('button', 'bk-bleeper bh-soot', undefined, layer);
    b.type = 'button';
    bleeper = b;
    bleeper.setAttribute('aria-label', 'Bleeper: open your book');
    bleeper.title = 'Your little black book (I)';
    const ico = el('span', 'ico', undefined, bleeper);
    ico.innerHTML = CONTROL_ICONS.pager;
    bleepCount = el('span', 'lcd', '--', bleeper);
    el('kbd', '', 'I', bleeper);
    bleeper.addEventListener('click', (e) => {
      e.preventDefault();
      if (screen) close();
      else open(inbox().unread > 0 ? 'messages' : undefined);
    });
  }

  function addKnob(): void {
    const tc = ctx.services.tryGet('touchControls');
    if (!tc) return;
    offs.push(
      tc.addSystemButton({
        id: 'book',
        label: 'Book',
        run: () => (screen ? close() : void open()),
      }),
    );
    const btn = ctx.uiRoot?.querySelector('.tc-sys-btn.sys-book') as HTMLElement | null;
    if (btn) {
      knobBadge = el('b', 'bk-badge', '', btn);
      knobBadge.hidden = true;
    }
  }

  function installKeys(): void {
    keyHandler = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyI' || e.repeat || e.ctrlKey || e.altKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (screen) return; // the UI routes I to the open book as "back"
      if (!ui()?.free || ctx.services.tryGet('online')?.panels.isOpen) return;
      if (open()) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', keyHandler, true);
  }

  /** D-pad down opens the book from the HUD (the UI's own pad navigation drives it once open). */
  function pollPad(): void {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
    let down = false;
    for (const p of navigator.getGamepads()) if (p?.connected && p.buttons[13]?.pressed) down = true;
    if (down && !padPrev && !screen && ui()?.free) open();
    padPrev = down;
  }

  return {
    id: 'book',
    order: 761,
    init(c) {
      ctx = c;
      load();
      const service: BookService = {
        get isOpen() {
          return !!screen;
        },
        get tab() {
          return screen?.view ?? null;
        },
        open,
        close,
        toggle: (tab) => (screen ? close() : void open(tab)),
        post,
        get unread() {
          return inbox().unread;
        },
        setOnlineProvider(p) {
          provider = p;
        },
        addSection(tab, section) {
          sections.set(tab, section);
          return () => {
            if (sections.get(tab) === section) sections.delete(tab);
          };
        },
        addJobsSource(source) {
          jobSources.push(source);
          return () => {
            const i = jobSources.indexOf(source);
            if (i >= 0) jobSources.splice(i, 1);
          };
        },
        refresh: () => screen?.refresh(),
        act,
      };
      c.services.provide('book', service);
      c.hooks.register('bookOpen', (tab) => open(tab as BookView | undefined));
      c.hooks.register('bookClose', () => close());
      c.hooks.register('bookPost', (from, subject, text, kind) => !!post({ from, subject, text, kind: (kind as PostInput['kind']) ?? 'story' }));
      c.hooks.register('bookState', () => {
        const box = inbox();
        const page = screen && screen.view !== 'home' ? pages.page(screen.tab) : null;
        const home = screen?.view === 'home' ? pages.home() : null;
        return {
          open: !!screen,
          tab: screen?.view ?? null,
          tabs: tabs(),
          tiles: home ? home.groups.flatMap((g) => g.tiles.map((t) => t.id)) : [],
          now: home?.now ? { kicker: home.now.kicker, title: home.now.title, actions: home.now.actions.map((a) => a.label) } : null,
          entries: page?.entries.map((e) => e.title) ?? [],
          selected: screen ? screen.index : null,
          unread: box.unread,
          scope: box === online ? 'online' : 'offline',
          messages: box.list().map((m) => ({ id: m.id, from: m.from, subject: m.subject, kind: m.kind, read: m.read })),
          bleeper: { shown: !!bleeper && getComputedStyle(bleeper).display !== 'none', count: bleepCount?.textContent ?? null, badge: knobBadge && !knobBadge.hidden ? knobBadge.textContent : null, bleeps },
        };
      });
      offs.push(
        c.events.on('newGameStarted', () => {
          offline.clear();
          persist();
          updateBleeper(true);
        }),
        c.events.on('netStatus', () => updateBleeper(true)),
      );
      if (c.uiRoot && typeof document !== 'undefined') {
        buildHud(c.uiRoot);
        installKeys();
      }
    },
    start() {
      if (!layer) return;
      addKnob();
      offs.push(wireOfflineFeed(ctx, (m) => (isOnline() ? null : post(m))));
      updateBleeper(true);
    },
    update() {
      if (!layer) return;
      const u = ui();
      const hudUp = !!u && (u.screen === 'hud' || u.screen === 'book') && ctx.state.mode !== 'title';
      toggle(layer, 'off', !hudUp);
      if (bleeper) toggle(bleeper, 'on-book', !!screen);
      updateBleeper();
      pollPad();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      if (burstTimer) window.clearTimeout(burstTimer);
      burstTimer = 0;
      burst = [];
      if (trailTimer) window.clearTimeout(trailTimer);
      trailTimer = 0;
      trail = [];
      if (keyHandler) window.removeEventListener('keydown', keyHandler, true);
      if (screen) close();
      layer?.remove();
    },
  };
}

declare module '../core/events' {
  interface GameEvents {
    /** The little black book was opened (onboarding counts it as "found the book"). */
    bookOpened: { tab: string };
  }
}

const KIND_KICKER: Record<BookMessage['kind'], string> = {
  story: 'A note',
  call: 'A phone call, written down',
  offer: 'Work going',
  warning: 'Warning',
  tip: 'A word to the wise',
  crew: 'Your crew',
  gang: 'Your gang',
  chat: 'Somebody said your name',
  system: 'Notice',
};

/** Keyboard wording in book text, reworded for the touch controls. */
function touchWords(text: string): string {
  if (!touchUi.enabled) return text;
  return text
    .replace(/\bPress E\b/g, 'Tap USE')
    .replace(/\bpress E\b/g, 'tap USE')
    .replace(/\bPress I\b/g, 'Tap the BOOK')
    .replace(/\bpress I\b/g, 'tap the BOOK')
    .replace(/\bpress M\b/g, 'tap MAP');
}
